<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'

import { T, useTranslate } from '@tolgee/vue'
import SpinsImage from '@/assets/images/fortune-wheel-cards/spin-bonus.webp'
import CoinsImage from '@/assets/images/prize-cards/green-card-background.webp'
import CoinsAndSpinsImage from '@/assets/images/fortune-wheel-cards/coin-and-spin.webp'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import { parseProfitHours } from '@/helpers/profit-time'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import { twMerge } from 'tailwind-merge'
import { useGiveaway } from '@/composables/game/giveaway'
import BaseButton from '@/components/base/BaseButton.vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { useScreenSize } from '@/composables/helpers/screen-size'

enum BonusType {
  DOUBLE_PROFIT,
  SPIN,
  COIN,
  COIN_AND_SPIN
}

const props = defineProps<{
  show: boolean
  doubleProfit: number
  spins: number
  tickets: number
  coins: number
}>()

defineEmits<{
  (e: 'ctaClick'): void
  (e: 'onCheckEntries'): void
}>()

const { topStyle } = useFullScreenDimensions()
const { t } = useTranslate()
const { countdown: giveawayCountdown } = useGiveaway()
const bonusType = ref<BonusType | null>(null)

const cardImagesMap = {
  [BonusType.DOUBLE_PROFIT]: CoinsImage,
  [BonusType.SPIN]: SpinsImage,
  [BonusType.COIN]: CoinsImage,
  [BonusType.COIN_AND_SPIN]: CoinsAndSpinsImage,
}

const prizeLabel = computed(() => {
  if (bonusType.value !== BonusType.DOUBLE_PROFIT) return ''
  const { hours, minutes, displayInHours } = parseProfitHours(props.doubleProfit)
  if (displayInHours) {
    return t.value('fortune_wheel.chest_prize.double_profit_hours_label', 'Profit per hour doubled for {count, plural, one {# hour} other {# hours}}!', { count: hours })
  }
  return t.value('fortune_wheel.chest_prize.double_profit_minutes_label', 'Profit per hour doubled for {minutes} minutes!', { minutes })
})

const currentImage = computed(() => {
  if (bonusType.value === null || !(bonusType.value in cardImagesMap)) return undefined
  return cardImagesMap[bonusType.value as keyof typeof cardImagesMap]
})

watch(() => props.show, show => {
  if (show) {
    document.body.classList.add('!overflow-hidden')
  } else {
    document.body.classList.remove('!overflow-hidden')
  }
}, { immediate: true })

useWatchOverlays(computed(() => props.show))

watch(() => [props.doubleProfit, props.coins, props.spins], () => {
  if (props.doubleProfit) {
    bonusType.value = BonusType.DOUBLE_PROFIT
  } else if (props.coins && props.spins) {
    bonusType.value = BonusType.COIN_AND_SPIN
  } else if (props.coins) {
    bonusType.value = BonusType.COIN
  } else if (props.spins) {
    bonusType.value = BonusType.SPIN
  } else {
    bonusType.value = null
  }
}, { immediate: true })
const { width } = useScreenSize()
</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show && currentImage && bonusType !== null"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.65] backdrop-blur-[4px] flex flex-col z-[800]"
      @click="$emit('ctaClick')"
    >
      <CrossCircleIcon
        class="w-6 h-6 min-w-[24px] absolute top-3 right-3 text-white/[87%]"
        :style="topStyle"
      />
      <div
        class="h-full w-full flex-1 flex flex-col gap-y-8 items-center justify-center relative"
      >
        <div
          class="w-[90%] h-0 pb-[90%] absolute rounded-full bg-teal-primary/[80] blur-[100px]"
        />
        <img
          class="absolute w-full h-auto"
          src="@/assets/images/goldwheel-overlay-bg.webp"
        >
        <div class="z-[2] w-full flex items-center justify-center relative">
          <div class="absolute flex flex-col items-center justify-center">
            <div
              class="relative w-full max-w-[368px] flex flex-col items-center justify-center h-full"
            >
              <div
                class=" text-shadow text-[36px] leading-[42px] font-bold min-w-max"
              >
                <T
                  v-if="giveawayCountdown && tickets"
                  key-name="bonus_overlay.friend_joined"
                  default-value="Your friend joined!"
                />
                <T
                  v-else
                  key-name="bonus_overlay.bonus_unlocked"
                  default-value="Bonus unlocked!"
                />
              </div>
              <div
                class="w-[200px] min-[390px]:w-[221px] my-8 max-w-full relative flex items-center justify-center overflow-hidden rounded-3xl"
                :style="(giveawayCountdown && tickets) ? 'height: auto': (width >= 390 ? 'height: 282px' : 'height: 254px')"
              >
                <img
                  v-if="giveawayCountdown && tickets"
                  src="@/assets/images/gifs/duck-partying.gif"
                  class="h-[220px]"
                >
                <template v-else>
                  <div class="absolute inset-0 size-full rounded-3xl border-2 border-white/20 z-10" />
                  <img
                    :src="currentImage"
                    class="absolute object-cover size-full object-center inset-0"
                  >
                </template>

                <div
                  v-if="!tickets || !giveawayCountdown"
                  :class="twMerge('absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center gap-y-6 w-full',
                                  spins && !coins && 'bottom-[30px] top-auto translate-y-0')"
                >
                  <div
                    v-if="coins"
                    class=" flex gap-1.5 items-center w-full justify-center"
                  >
                    <img
                      :src="CoinIcon"
                      class="h-7 w-auto object-contain"
                    >
                    <span
                      class="text-[32px] leading-[25px] font-[900] text-white black-stroke disable-slashed-zero"
                    >
                      {{
                        formatNumberFullTruncate(coins)
                      }}
                    </span>
                  </div>
                  <div v-if="coins && spins">
                    <span class="black-stroke text-[32px] leading-[25px] font-[900] ">+</span>
                  </div>
                  <div
                    v-if="spins && coins"
                    class="black-stroke text-[32px] leading-[25px] font-[900]"
                  >
                    <T
                      key-name="bonus_overlay.count_spin"
                      default-value="{count, plural, one {# spin} other {# spins}}"
                      :params="{count: spins}"
                    />
                  </div>
                  <div
                    v-else-if="spins && !coins"
                    class="black-title-golden-stroke pb-2"
                  >
                    <T
                      key-name="bonus_overlay.count_spin"
                      :default-value="`{count, plural, one {#\nspin} other {#\nspins}}`"
                      :params="{count: spins}"
                    />
                  </div>
                  <div
                    v-else-if="doubleProfit"
                    class="black-title-golden-stroke"
                  >
                    <T
                      key-name="bonus_overlay.count_spin"
                      :default-value="`PPH \n x2`"
                    />
                  </div>
                </div>
              </div>

              <div
                class="flex flex-col items-center w-screen max-w-[368px]"
                :class="giveawayCountdown && tickets && 'gap-y-3'"
              >
                <div class="text-white/60 text-2xl font-medium">
                  <T
                    v-if="giveawayCountdown && tickets"
                    key-name="fortune_wheel.chest_prize.you_get"
                    default-value="You get"
                  />
                  <T
                    v-else
                    key-name="fortune_wheel.chest_prize.you_won"
                    default-value="You won"
                  />
                </div>
                <div
                  class="flex items-center text-light-white text-[28px] leading-8 font-bold -tracking-[0.56px] text-center"
                  :class="giveawayCountdown && tickets && 'flex-col items-center gap-y-1.5'"
                >
                  <div
                    v-if="coins"
                    class="flex gap-1.5 items-center"
                    v-tooltip="{content: formatUnshortenedNumber(coins), placement: 'bottom'}"
                  >
                    <img
                      :src="CoinIcon"
                      class="h-7 w-auto"
                    >
                    <span
                      class="leading-[25px] font-[700] text-white disable-slashed-zero"
                    >
                      {{
                        formatNumberFullTruncate(coins)
                      }}
                    </span>
                  </div>
                  <span
                    v-if="coins && spins"
                    class="leading-[25px] font-[700] text-white whitespace-break-spaces"
                  >
                    <T
                      key-name="fortune_wheel.info.more_spins.and"
                      default-value=" and "
                    />
                  </span>
                  <div v-if="spins">
                    <T
                      key-name="bonus_overlay.count_spin"
                      default-value="{count, plural, one {# spin} other {# spins}}"
                      :params="{count: spins}"
                    />
                  </div>
                  <div
                    v-else-if="tickets"
                    class="flex items-center gap-x-3 justify-center"
                  >
                    <img
                      src="@/assets/images/emojis/emoji-ticket.png"
                      class="size-8"
                    >
                    <span>
                      <T
                        key-name="bonus_overlay.count_tickets"
                        default-value="{count, plural, one {# giveaway ticket} other {# giveaway tickets}}"
                        :params="{count: tickets}"
                      />
                    </span>
                  </div>
                  <div
                    v-else
                  >
                    {{ prizeLabel }}
                  </div>
                </div>
              </div>
              <div
                v-if="giveawayCountdown && tickets"
                @click.stop.prevent="$emit('ctaClick');$emit('onCheckEntries')"
                class="mt-8"
              >
                <BaseButton
                  class="min-w-[286px]"
                >
                  <T
                    key-name="fortune_wheel.ticket_overlay.cta"
                    default-value="CHECK MY ENTRIES"
                  />
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
  opacity: 0;
}

.scale-in-image {
  animation: scale-in-image 0.3s ease-out;
  filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.7));
}

.animate-blur {
  animation: blur-image 1s ease-out forwards;
  animation-delay: 0.4s;
}

@keyframes scale-in-image {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  20% {
    opacity: 1; /* Ensure it starts becoming visible */
  }
  70% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes blur-image {
  0% {
    filter: blur(0);
  }
  100% {
    filter: blur(5px);
  }
}

.text-shadow {
  text-shadow: 0px 0px 24px rgba(0, 0, 0, 0.44)
}

.black-stroke {
  text-shadow: -1.5px -1.5px 0 #000,
  1.5px -1.5px 0 #000,
  -1.5px 1.5px 0 #000,
  1.5px 1.5px 0 #000,
  -2px 0 0 #000,
  2px 0 0 #000,
  0 -2px 0 #000,
  0 2px 0 #000,
  -1px -1px 0 #000, /* Finer shadow adjustments */
  1px -1px 0 #000,
  -1px 1px 0 #000,
  1px 1px 0 #000;
}
</style>